import { triggerToast } from "./toaster";
import {
    readItem,
    readPagePayload,
    track,
    trackItemEvent,
} from "./tracking";

const state = {
    shoppingCartLabel: undefined,
    /** @type {HTMLFormElement} */
    basketListForm: undefined,
};

function refreshBasketInfo() {
    fetch("/cart/info")
        .then((response) => response.json())
        .then((data) => {
            state.shoppingCartLabel.innerText = data.piecesCount;
            setDisabledOnBasketFormInputs(false);
        });
}

function setDisabledOnBasketFormInputs(disabled = true) {
    if (!state.basketListForm) {
        return false;
    }

    // biome-ignore lint/complexity/noForEach: <explanation>
    // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
    state.basketListForm
        .querySelectorAll("input")
        .forEach((item) => (item.disabled = disabled));
    // biome-ignore lint/complexity/noForEach: <explanation>
    // biome-ignore lint/suspicious/noAssignInExpressions: <explanation>
    state.basketListForm
        .querySelectorAll("button")
        .forEach((item) => (item.disabled = disabled));

    if (disabled === false) {
        state.basketListForm
            .querySelectorAll(".minus-button")
            .forEach(
                (item) =>
                    (item.disabled =
                        parseInt(
                            item.parentElement.querySelector(".number-input")
                                .value,
                        ) < 2),
            );
    }
}

function setProductCount(form) {}

/**
 * Use as button onclick action in a form context
 * @param {HTMLFormElement} form
 */
function addProductToCart(form) {
    const statusSpan = form.querySelector('button > [role="status"]');
    const formData = new FormData(form);

    statusSpan.classList.remove("d-none");

    fetch(form.action, {
        method: "POST",
        body: formData,
    })
        .then((result) => result.json())
        .then((data) => {
            triggerToast(data.text);
            trackItemEvent("add_to_cart", readItem(form));
        })
        .finally(() => {
            setTimeout(() => statusSpan.classList.add("d-none"), 200);
            refreshBasketInfo();
        });
}

/**
 *
 * @param {HTMLFormElement} form
 */
function updateCartProducts() {
    const formData = new FormData(state.basketListForm);

    setDisabledOnBasketFormInputs();
    fetch(state.basketListForm.action, {
        method: "POST",
        body: formData,
    })
        .then((result) => result.json())
        .then((data) => {
            triggerToast(data.text);
        })
        .finally(() => refreshBasketInfo());
}

function applyCustomFormSubmit(callback) {
    return (form) => {
        form.addEventListener("submit", (e) => {
            e.preventDefault();
            e.stopPropagation();
            callback(form);
        });
    };
}

/**
 *
 * @param {HTMLButtonElement} element
 */
function removeProductFromCart(element) {
    const productId = element.dataset.productId;
    const confirmMessage = element.dataset.confirmMessage;

    if (!window.confirm(confirmMessage)) {
        return;
    }

    const formData = new FormData(state.basketListForm);

    for (const entry of formData.entries()) {
        entry[0].endsWith(`[${productId}]`) && formData.set(entry[0], -1);
    }
    setDisabledOnBasketFormInputs();

    const row = document.getElementById(`basketItem[${productId}]`);
    const removedItem = readItem(row);
    const removedCount = Number.parseInt(
        row?.querySelector(".number-input")?.value ?? "1",
    );

    fetch(state.basketListForm.action, {
        method: "POST",
        body: formData,
    })
        .then((result) => result.json())
        .then((data) => {
            triggerToast(data.text);
            trackItemEvent(
                "remove_from_cart",
                removedItem,
                Number.isNaN(removedCount) ? 1 : removedCount,
            );
        })
        .then(() => {
            row?.remove();
        })
        .finally(() => refreshBasketInfo());
}

/**
 * A quantity change is an add or a remove of the difference, per GA4 semantics.
 * @param {HTMLInputElement} numInput
 */
function trackQuantityChange(numInput) {
    const previous = Number.parseInt(numInput.dataset.gaPrevious ?? "0");
    const current = Number.parseInt(numInput.value);
    numInput.dataset.gaPrevious = numInput.value;

    if (Number.isNaN(previous) || Number.isNaN(current) || previous === current) {
        return;
    }

    const item = readItem(numInput.closest("[data-ga-item]"));
    const delta = current - previous;

    trackItemEvent(
        delta > 0 ? "add_to_cart" : "remove_from_cart",
        item,
        Math.abs(delta),
    );
}

function trackBeginCheckout() {
    const form = document.querySelector("[data-ga-checkout-form]");
    const payload = readPagePayload();

    if (!form || !payload) {
        return;
    }

    form.addEventListener("submit", () => track("begin_checkout", payload));
}

function initializeBasketModule() {
    state.shoppingCartLabel = document.getElementById("BasketLabel");
    refreshBasketInfo();
    document
        .querySelectorAll("form[id^=shop_add_to_cart_form_]")
        .forEach(applyCustomFormSubmit(addProductToCart));

    trackBeginCheckout();

    state.basketListForm = document.getElementById("shop_update_cart_form");

    if (state.basketListForm) {
        state.basketListForm.addEventListener("submit", (e) => {
            e.stopPropagation();
            e.preventDefault();
        });

        document.querySelectorAll(".remove-button").forEach((element) => {
            element.addEventListener("click", () =>
                removeProductFromCart(element),
            );
        });

        // biome-ignore lint/complexity/noForEach: <explanation>
        document
            .querySelectorAll("[data-name='subform']")
            .forEach((inputWrapper) => {
                /**
                 * @type {HTMLInputElement}
                 */
                const numInput = inputWrapper.querySelector(".number-input");

                inputWrapper
                    .querySelector(".plus-button")
                    .addEventListener("click", () => {
                        numInput.value = Number.parseInt(numInput.value) + 1;
                        numInput.dispatchEvent(new Event("change"));
                    });

                inputWrapper
                    .querySelector(".minus-button")
                    .addEventListener("click", () => {
                        if (Number.parseInt(numInput.value) === 1) {
                            return false;
                        }

                        numInput.value = Math.max(
                            1,
                            Number.parseInt(numInput.value) - 1,
                        );
                        numInput.dispatchEvent(new Event("change"));
                    });

                numInput.dataset.gaPrevious = numInput.value;

                numInput.addEventListener("change", function (e) {
                    try {
                        const parsed = Number.parseInt(this.value);
                        numInput.value = Number.isNaN(parsed)
                            ? 1
                            : Math.max(1, parsed);
                    } catch (error) {
                        numInput.value = 1;
                    }

                    trackQuantityChange(numInput);
                    updateCartProducts();
                });
            });

        state.basketListForm.addEventListener("submit", (e) => {
            e.preventDefault();
            e.stopPropagation();
        });
    }
}

export { initializeBasketModule };
